import { BrokerJob, BrokerService } from '@warehouse/object-browser/feature-broker-core';

/**
 * What a Broker job id names. The id is content-addressed: `serviceUUID:serviceVersion:<key values>` where the key
 * values follow the service's `key` paths (`services.json`), reference values first, `params.*` values after,
 * whatever order the `key` lists them in (the Broker's `brokerKeyValues` in `content-key.ts` puts the reference values
 * ahead of the params). A single-reference job therefore carries its reference key in the third segment:
 * - `references[*].nxObject.uuid`  → one object (S3 Copy, Delete, S3 Restore)
 * - `references[*].nxObject.eTag`  → content: every object with that eTag (Media Info, NxRay, Hash, Mezz Proxy)
 * Multi-object (`combineKey`) jobs collapse to `Nitems:<hash>` and are not resolvable from the id.
 */
export type JobReference =
	| { kind: 'object'; nxObjectUuid: string }
	| { kind: 'content'; eTag: string }
	| { kind: 'unknown' };

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** S3 eTags: an MD5 hex, with a `-N` part count suffix for multipart uploads. */
const ETAG_PATTERN = /^[0-9a-f]{32}(-\d+)?$/i;
const COMBINED_KEY_PATTERN = /^\d+items$/;

const REFERENCE_KEY_PATHS: Record<string, Exclude<JobReference, { kind: 'unknown' }>['kind']> = {
	'references[*].nxObject.uuid': 'object',
	'references[*].nxObject.eTag': 'content',
};

export const JobReferenceParser = {
	parse(job: BrokerJob, service: BrokerService | undefined): JobReference {
		const referencePath = service?.key?.find((path) => path in REFERENCE_KEY_PATHS);
		if (!referencePath || !job.serviceUUID) return { kind: 'unknown' };

		const [serviceUUID, , value] = job.jobId.split(':');
		if (serviceUUID !== job.serviceUUID || !value || COMBINED_KEY_PATTERN.test(value)) return { kind: 'unknown' };

		const kind = REFERENCE_KEY_PATHS[referencePath];
		if (kind === 'object') return UUID_PATTERN.test(value) ? { kind, nxObjectUuid: value } : { kind: 'unknown' };
		return ETAG_PATTERN.test(value) ? { kind, eTag: value } : { kind: 'unknown' };
	},
};

/** The first 8 and the last 5 characters: enough to recognise an eTag in a grid cell. */
export function shortETag(eTag: string): string {
	return eTag.length <= 16 ? eTag : `${eTag.slice(0, 8)}…${eTag.slice(-5)}`;
}
